import { useState, useCallback } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAppSnackbar } from '../contexts/SnackbarContext';

export function useNotificationActions() {
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useAppSnackbar();
  const context = useNotifications();

  const markAsRead = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      // Instead of optimistic updates, just call the context's method directly
      await context.markAsRead(id);
    } catch (error) {
      showError('Failed to mark notification as read');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [context, showError]);

  const markAllAsRead = useCallback(async () => {
    setIsLoading(true);
    try {
      await context.markAllAsRead();
    } catch (error) {
      showError('Failed to mark all notifications as read');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [context, showError]);

  const clearNotification = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      await context.clearNotification(id);
    } catch (error) {
      showError('Failed to clear notification');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [context, showError]);

  return {
    isLoading,
    markAsRead,
    markAllAsRead,
    clearNotification
  };
}

export type UseNotificationActionsReturn = ReturnType<typeof useNotificationActions>;

